import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, X, ArrowRight, FolderTree, ShieldAlert, Briefcase, Database, FileText } from "lucide-react";

/**
 * DemoSubmissionBanner — operational mission-notification banner.
 *
 * Shown at the top of /admin, /pm, /shop hubs when the visitor has just
 * completed the /demo Toolbox Talk QR Flow. Reads the persisted record
 * from localStorage and renders a SUBTLE industrial banner — NOT a
 * SaaS celebration modal.
 *
 *   ✔ TOOLBOX TALK ROUTED · TRENCH SAFETY · CREW A · 9 ATTENDEES
 *   Saved to Safety Records · PM + Safety notified · Backup mirror
 *   [view record] [open meetings dashboard]
 *
 * Storage key:  forgeops.demo.lastSubmission
 * Dismiss persists per-session (sessionStorage flag).
 *
 * Tone: a HUD strip. Mission notification. Restraint over flash.
 */
const STORAGE_KEY  = "forgeops.demo.lastSubmission";
const DISMISS_KEY  = "forgeops.demo.lastSubmission.dismissed";

export default function DemoSubmissionBanner({ portal = "admin" }) {
  const [sub, setSub] = useState(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const dismissed = window.sessionStorage.getItem(DISMISS_KEY);
      if (dismissed === raw) return; // user dismissed THIS specific submission
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.kind) return;
      // Auto-expire after 30 minutes so the banner doesn't haunt the demo
      const ageMs = Date.now() - new Date(parsed.at).getTime();
      if (Number.isFinite(ageMs) && ageMs > 30 * 60 * 1000) return;
      setSub(parsed);
    } catch {
      /* swallow */
    }
  }, []);

  const dismiss = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY) || "";
      window.sessionStorage.setItem(DISMISS_KEY, raw);
    } catch { /* */ }
    setSub(null);
  };

  if (!sub) return null;

  const meetingsHref = portal === "pm" ? "/pm" : "/admin/meetings";
  const recordHref   = portal === "pm" ? "/pm" : "/admin/meetings";

  // Build the routed-destinations chip set
  const dests = sub.routedTo && sub.routedTo.length
    ? sub.routedTo
    : ["PM Folder", "Safety Folder", "Job Folder", "Admin Records", "Backup"];

  const sinceLabel = (() => {
    try {
      const m = Math.max(0, Math.round((Date.now() - new Date(sub.at).getTime()) / 60000));
      if (m < 1) return "just now";
      if (m < 60) return `${m}m ago`;
      const h = Math.round(m / 60);
      return `${h}h ago`;
    } catch { return ""; }
  })();

  return (
    <div
      data-testid="demo-submission-banner"
      className="border-b"
      style={{
        background:
          "linear-gradient(180deg, rgba(46,125,50,0.16), rgba(46,125,50,0.06)), #0B0B0B",
        borderColor: "rgba(46,125,50,0.45)",
      }}
    >
      {/* Caution-stripe accent for industrial tone */}
      <div className="h-0.5 w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(46,125,50,0.7), transparent)" }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-wrap">
          {/* Status icon */}
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0 mt-0.5 sm:mt-0"
            style={{
              background: "rgba(46,125,50,0.22)",
              color: "#65DA76",
              border: "1px solid rgba(46,125,50,0.6)",
            }}
            data-testid="dsb-icon"
          >
            <CheckCircle2 className="w-5 h-5" />
          </span>

          {/* Headline + meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#65DA76" }}>
                {sub.kind?.toUpperCase()} ROUTED
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
                · {sinceLabel} · your demo submission
              </span>
            </div>
            <div className="font-display text-sm sm:text-base font-bold text-white leading-tight mt-0.5 truncate">
              {sub.topic || "—"}
              {sub.crewLabel ? <span className="opacity-80"> · {sub.crewLabel}</span> : null}
              {sub.attendees ? <span className="opacity-80"> · {sub.attendees} attendees</span> : null}
              {sub.jobNumber ? <span className="opacity-80"> · {sub.jobNumber}</span> : null}
            </div>

            {/* Routed-to chips — operational, not flashy */}
            <div className="flex flex-wrap items-center gap-1 mt-1.5">
              {dests.slice(0, 5).map((d) => {
                const Icon = d.toLowerCase().includes("safety") ? ShieldAlert
                          : d.toLowerCase().includes("pm") ? Briefcase
                          : d.toLowerCase().includes("backup") ? Database
                          : d.toLowerCase().includes("admin") ? Database
                          : FolderTree;
                return (
                  <span
                    key={d}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[9px] uppercase tracking-[0.2em]"
                    style={{
                      background: "rgba(46,125,50,0.10)",
                      color: "#65DA76",
                      border: "1px solid rgba(46,125,50,0.35)",
                    }}
                  >
                    <Icon className="w-2.5 h-2.5" />
                    {d}
                  </span>
                );
              })}
            </div>
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to={recordHref}
              data-testid="dsb-view-record"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded font-mono text-[10px] uppercase tracking-[0.2em] font-bold transition"
              style={{
                background: "rgba(255,106,0,0.14)",
                color: "#FF6A00",
                border: "1px solid rgba(255,106,0,0.5)",
              }}
            >
              <FileText className="w-3 h-3" /> View record
            </Link>
            <Link
              to={meetingsHref}
              data-testid="dsb-open-dashboard"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded font-mono text-[10px] uppercase tracking-[0.2em] transition"
              style={{
                background: "transparent",
                color: "#BDBDBD",
                border: "1px solid #2A2A2A",
              }}
            >
              Open dashboard <ArrowRight className="w-3 h-3" />
            </Link>
            <button
              type="button"
              onClick={dismiss}
              data-testid="dsb-dismiss"
              aria-label="Dismiss notification"
              className="inline-flex items-center justify-center w-8 h-8 rounded transition"
              style={{ background: "transparent", color: "#8A8A8A", border: "1px solid #2A2A2A" }}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
